import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import SelectAllClearToggles from '../../../../components/v2/SelectAllClearToggles.jsx';

/**
 * BulkAreaGroupDrawer — set area group membership for several areas at once.
 *
 * Mirrors the residents' bulk group drawer. Whatever is checked becomes the
 * complete group list for every selected area, matching both the backend
 * (sp_tlGroupAreas replaces membership) and the single-area edit drawer. Groups
 * left unchecked are removed.
 *
 * Starts with nothing checked: with several areas selected there is no single
 * "current" set to pre-fill, so the per-group counts ("in 2 of 3 selected")
 * show the existing state instead. Areas that already match the checked set are
 * dropped from the payload so no pointless writes are sent. Saving with nothing
 * checked is valid — it clears all groups.
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   onSave: (changes: Array<{area_id, groupIds, oldGroupIds}>) => Promise<void>,
 *   areaGroups: Array<{ area_group_id: number, area_group_name: string }>,
 *   areas: Array<object>,   // the SELECTED area rows
 *   saving: boolean,
 * }} props
 */

const idsOf = (area) => (area?.area_groups || [])
  .map((g) => g.area_group_id)
  .filter((id) => id != null);

const sameSet = (a, b) => {
  if (a.length !== b.length) return false;
  const sa = [...a].sort((x, y) => x - y);
  const sb = [...b].sort((x, y) => x - y);
  return sa.every((v, i) => v === sb[i]);
};

const BulkAreaGroupDrawer = ({ isOpen, onClose, onSave, areaGroups, areas, saving }) => {
  const drawerRef = useRef(null);
  const [checkedIds, setCheckedIds] = useState([]);

  useEffect(() => {
    if (isOpen) setCheckedIds([]);
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const selected = areas || [];
  const sortedGroups = useMemo(
    () => [...(areaGroups || [])].sort((a, b) =>
      (a.area_group_name || '').localeCompare(b.area_group_name || '', undefined, { sensitivity: 'base' })),
    [areaGroups],
  );

  /** How many of the selected areas are already in each group */
  const membershipCounts = useMemo(() => {
    const counts = {};
    selected.forEach((a) => {
      idsOf(a).forEach((id) => { counts[id] = (counts[id] || 0) + 1; });
    });
    return counts;
  }, [selected]);

  const toggleGroup = useCallback((groupId) => {
    setCheckedIds((prev) => (prev.includes(groupId)
      ? prev.filter((id) => id !== groupId)
      : [...prev, groupId]));
  }, []);

  /** The checked set becomes every selected area's full list; no-ops dropped. */
  const changes = useMemo(() => selected
    .map((a) => ({
      area_id: a.area_id,
      groupIds: [...checkedIds],
      oldGroupIds: idsOf(a),
    }))
    .filter((c) => !sameSet(c.groupIds, c.oldGroupIds)), [selected, checkedIds]);

  const hasWork = changes.length > 0;
  const isClearAll = checkedIds.length === 0 && changes.length > 0;

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!hasWork) return;
    onSave(changes);
  }, [hasWork, changes, onSave]);

  const summary = (() => {
    // Worded as a statement of fact, never as "pick a group" — saving zero
    // groups is valid, so the UI must not imply one is required.
    if (changes.length === 0) {
      if (selected.length === 0) return 'No areas selected.';
      return checkedIds.length === 0
        ? 'The selected areas already have no groups — nothing to apply.'
        : 'No changes — the selected areas already have exactly these groups.';
    }
    const n = changes.length;
    const who = `${n} area${n !== 1 ? 's' : ''}`;
    if (isClearAll) return `Warning: removes all groups from ${who}.`;
    return `Sets ${checkedIds.length} group${checkedIds.length !== 1 ? 's' : ''} on ${who}. Groups not checked will be removed.`;
  })();

  return (
    <>
      {isOpen && <div className="ap-drawer-backdrop" onClick={onClose} aria-hidden="true" />}

      <div
        ref={drawerRef}
        className={`ap-drawer ap-drawer--md ${isOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Edit groups for selected areas"
      >
        <div className="ap-drawer__header">
          <h2 className="ap-drawer__title">Edit Area Groups</h2>
          <button type="button" onClick={onClose} className="ap-drawer__close" aria-label="Close drawer">
            <svg className="ap-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="ap-form">
          <div className="ap-form__body">
            <p className="ap-field__label" style={{ marginBottom: 0 }}>
              {selected.length} area{selected.length !== 1 ? 's' : ''} selected
            </p>

            <div>
              <div className="sac-field-header">
                <p className="ap-field__label">Area Groups</p>
                {sortedGroups.length > 0 ? (
                  <SelectAllClearToggles
                    onSelectAll={() => setCheckedIds(sortedGroups.map((g) => g.area_group_id))}
                    onClear={() => setCheckedIds([])}
                    allSelected={checkedIds.length === sortedGroups.length}
                    noneSelected={checkedIds.length === 0}
                  />
                ) : null}
              </div>

              {sortedGroups.length === 0 ? (
                <p className="ap-cell-text--muted">No area groups exist for this community yet.</p>
              ) : (
                <div className="ap-group-grid">
                  {sortedGroups.map((group) => {
                    const isChecked = checkedIds.includes(group.area_group_id);
                    const count = membershipCounts[group.area_group_id] || 0;
                    return (
                      <label
                        key={group.area_group_id}
                        className={`ap-group-option ${isChecked ? 'is-selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleGroup(group.area_group_id)}
                          className="ap-checkbox"
                        />
                        <span className="ap-group-option__text">
                          {group.area_group_name}
                          <span style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                            {count === 0
                              ? 'none of the selected'
                              : `in ${count} of ${selected.length} selected`}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <p className={`ap-bulk-summary${isClearAll ? ' ap-bulk-summary--danger' : ''}`}>
              {summary}
            </p>
          </div>

          <div className="ap-form__footer">
            <button type="button" onClick={onClose} className="ap-btn ap-btn--cancel">
              Cancel
            </button>
            <button type="submit" disabled={!hasWork || saving} className="ap-btn ap-btn--primary">
              {saving ? 'Saving...' : 'Apply'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default BulkAreaGroupDrawer;
